import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarStar, Copy, WhatsappLogo, ArrowsClockwise, Trash, PencilSimple, Receipt, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: '', description: '', deadline: '', closes_on: '', price_student_early: '', price_guest_early: '', price_student_late: '', price_guest_late: '' };

export default function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/paid-events`, { withCredentials: true });
      setEvents(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchEvents(); }, []);

  const linkFor = (id) => `${window.location.origin}/evento/${id}`;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome obrigatório'); return; }
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/admin/paid-events/${editId}`, form, { withCredentials: true });
      else await axios.post(`${API}/admin/paid-events`, form, { withCredentials: true });
      toast.success(editId ? 'Evento atualizado' : 'Evento criado');
      setForm(empty); setEditId(null); fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const edit = (ev) => {
    setEditId(ev.id);
    setForm({ name: ev.name || '', description: ev.description || '', deadline: ev.deadline || '', closes_on: ev.closes_on || '', price_student_early: ev.price_student_early ?? '', price_guest_early: ev.price_guest_early ?? '', price_student_late: ev.price_student_late ?? '', price_guest_late: ev.price_guest_late ?? '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const remove = async (id) => { if (!window.confirm('Excluir evento?')) return; await axios.delete(`${API}/admin/paid-events/${id}`, { withCredentials: true }); fetchEvents(); };
  const copyLink = (id) => { navigator.clipboard?.writeText(linkFor(id)); toast.success('Link copiado'); };
  const whatsapp = (ev) => { const msg = encodeURIComponent(`Pague o evento "${ev.name}": ${linkFor(ev.id)}`); window.open(`https://wa.me/?text=${msg}`, '_blank'); };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="events-admin-page">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><CalendarStar size={26} className="text-[#E85D04]" /> Eventos / Pagamentos Avulsos</h1>
          <p className="text-[#73706A] text-sm mt-1">Crie um evento com preços de aluno e convidado. O link pode ser enviado a várias pessoas e também aparece no Portal do Aluno.</p>
        </div>

        <form onSubmit={save} className="bg-white border border-[#E6E2D8] rounded-xl p-5 space-y-4">
          <p className="font-semibold text-[#1A1A1A]">{editId ? 'Editar evento' : 'Novo evento'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Nome do evento *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Festa 5 anos" data-testid="event-name-field" /></div>
            <div><Label>Descrição</Label><Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Ex: Festa de aniversário" data-testid="event-desc-field" /></div>
          </div>
          <div>
            <Label>Inscrições e pagamento até (opcional)</Label>
            <Input type="date" value={form.closes_on} onChange={e => set('closes_on', e.target.value)} data-testid="event-closes-on-field" />
            <p className="text-xs text-[#73706A] mt-1">Depois desta data o evento continua visível, mas com "Inscrições encerradas".</p>
          </div>
          <div>
            <Label>Prazo do preço promocional (opcional)</Label>
            <Input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} data-testid="event-deadline-field" />
            <p className="text-xs text-[#73706A] mt-1">Até esta data vale o preço promocional. Depois, o preço cheio. Deixe vazio para preço único.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Aluno (promo)</Label><Input type="number" step="0.01" value={form.price_student_early} onChange={e => set('price_student_early', e.target.value)} placeholder="25" data-testid="event-pse" /></div>
            <div><Label className="text-xs">Convidado (promo)</Label><Input type="number" step="0.01" value={form.price_guest_early} onChange={e => set('price_guest_early', e.target.value)} placeholder="30" data-testid="event-pge" /></div>
            <div><Label className="text-xs">Aluno (cheio)</Label><Input type="number" step="0.01" value={form.price_student_late} onChange={e => set('price_student_late', e.target.value)} placeholder="30" data-testid="event-psl" /></div>
            <div><Label className="text-xs">Convidado (cheio)</Label><Input type="number" step="0.01" value={form.price_guest_late} onChange={e => set('price_guest_late', e.target.value)} placeholder="35" data-testid="event-pgl" /></div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="event-save-btn">{saving ? 'Salvando...' : (editId ? 'Atualizar evento' : 'Criar evento')}</Button>
            {editId && <Button type="button" variant="outline" onClick={() => { setForm(empty); setEditId(null); }}>Cancelar</Button>}
          </div>
        </form>

        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E6E2D8] flex items-center justify-between">
            <span className="font-semibold text-[#1A1A1A]">{loading ? 'Carregando...' : `${events.length} evento(s)`}</span>
            <Button variant="outline" size="sm" onClick={fetchEvents}><ArrowsClockwise size={16} className="mr-1" /> Atualizar</Button>
          </div>
          <div className="divide-y divide-[#F0EDE4]">
            {events.map((ev, i) => (
              <div key={ev.id} className="p-4" data-testid={`event-row-${i}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{ev.name}</p>
                    <p className="text-xs text-[#73706A]">Aluno ${ev.price_student?.toFixed(2)} · Convidado ${ev.price_guest?.toFixed(2)} {ev.deadline ? `· promo até ${new Date(ev.deadline + 'T00:00:00').toLocaleDateString('pt-BR')}` : ''}</p>
                    {ev.closes_on && <p className={`text-xs font-semibold ${ev.closed ? 'text-[#9B2226]' : 'text-[#2D6A4F]'}`} data-testid={`event-closes-label-${ev.id}`}>{ev.closed ? 'Inscrições encerradas' : `Inscrições até ${new Date(ev.closes_on + 'T00:00:00').toLocaleDateString('pt-BR')}`}</p>}
                    <p className="text-xs text-[#2D6A4F] font-semibold mt-1">{ev.paid_count} pago(s) · total ${ev.total_paid?.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyLink(ev.id)} title="Copiar link" className="p-2 rounded-lg hover:bg-[#F5F2EB] text-[#0077B6]" data-testid={`event-copy-${i}`}><Copy size={16} /></button>
                    <button onClick={() => whatsapp(ev)} title="WhatsApp" className="p-2 rounded-lg hover:bg-[#F0FDF4] text-[#25D366]"><WhatsappLogo size={16} weight="fill" /></button>
                    <button onClick={() => edit(ev)} title="Editar" className="p-2 rounded-lg hover:bg-[#F5F2EB] text-[#73706A]" data-testid={`event-edit-${i}`}><PencilSimple size={16} /></button>
                    <button onClick={() => remove(ev.id)} title="Excluir" className="p-2 rounded-lg hover:bg-red-50 text-[#9B2226]"><Trash size={16} /></button>
                    <button onClick={() => setExpanded(expanded === ev.id ? null : ev.id)} className="p-2 rounded-lg hover:bg-[#F5F2EB] text-[#73706A]" data-testid={`event-expand-${i}`}><CaretDown size={16} /></button>
                  </div>
                </div>
                {expanded === ev.id && (
                  <div className="mt-3 bg-[#FAF8F3] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#73706A] mb-2">Quem pagou ({ev.payers?.length || 0}):</p>
                    {ev.payers?.length ? (
                      <table className="w-full text-xs">
                        <thead className="text-[#73706A]"><tr><th className="text-left py-1">Nome</th><th className="text-left">Tipo</th><th className="text-right">Valor</th><th className="text-left pl-3">Método</th><th></th></tr></thead>
                        <tbody>
                          {ev.payers.map((p, j) => (
                            <tr key={j} className="border-t border-[#F0EDE4]" data-testid={`payer-${i}-${j}`}>
                              <td className="py-1 text-[#1A1A1A] font-medium">{p.name}</td>
                              <td className="capitalize">{p.type}</td>
                              <td className="text-right">${p.amount?.toFixed(2)}</td>
                              <td className="pl-3 text-[#73706A]">{p.method}</td>
                              <td className="text-right">{p.has_receipt && <a href={`${API}/admin/event-payments/${p.payment_id}/receipt`} target="_blank" rel="noopener noreferrer" className="text-[#2D6A4F]"><Receipt size={14} /></a>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <p className="text-xs text-[#9CA3AF]">Ninguém pagou ainda.</p>}
                  </div>
                )}
              </div>
            ))}
            {!loading && events.length === 0 && <p className="px-5 py-8 text-center text-[#73706A]">Nenhum evento. Crie o primeiro acima.</p>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
